import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Mic, MicOff, Phone, PhoneOff, Loader2, AlertTriangle, X } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const langToBCP47 = (lang) => {
  if (!lang) return 'en-GB';
  const map = { en: 'en-GB', ur: 'ur-PK', hi: 'hi-IN', pa: 'pa-IN' };
  return map[lang] || 'en-GB';
};

/**
 * Browser-based test widget for the Flight 380 AI Voice Agent (Sarah).
 *   - OpenAI TTS via /api/voice-agent/tts (coral voice) with browser fallback
 *   - Web Speech API for caller ASR (Chrome / Edge desktop)
 *   - Calls /api/voice-agent/turn + /finalize on the backend
 *   - When the AI signals `transfer_to_human=true` we show a short notice and
 *     end the test call. (On the real 3CX bridge, this is where the SIP
 *     re-INVITE / REFER is sent so the call goes to 01908 220000.)
 */
export const VoiceAgentTestWidget = ({ onClose }) => {
  const [callActive, setCallActive] = useState(false);
  const [listening, setListening] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [transcript, setTranscript] = useState([]);
  const [lang, setLang] = useState('en');
  const [error, setError] = useState(null);
  const [supported, setSupported] = useState(true);
  const [micStatus, setMicStatus] = useState('unknown'); // 'unknown' | 'granted' | 'denied' | 'prompt'

  const callIdRef = useRef(null);
  const recognitionRef = useRef(null);
  const audioRef = useRef(null);
  const recognitionActiveRef = useRef(false);
  const langRef = useRef('en');

  // Keep langRef in sync so listenOnce always uses the latest language
  useEffect(() => { langRef.current = lang; }, [lang]);

  // Initialise SpeechRecognition once
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setSupported(false); return; }
    const r = new SR();
    r.continuous = false;
    r.interimResults = false;
    r.lang = langToBCP47('en');
    r.onstart = () => { recognitionActiveRef.current = true; setListening(true); };
    r.onend = () => { recognitionActiveRef.current = false; setListening(false); };
    r.onerror = (e) => {
      recognitionActiveRef.current = false;
      setListening(false);
      if (e?.error && e.error !== 'no-speech' && e.error !== 'aborted') {
        setError(`Mic error: ${e.error}`);
      }
    };
    recognitionRef.current = r;
    return () => { try { r.abort(); } catch { /* ignore */ } };
  }, []);

  const stopSpeaking = useCallback(() => {
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch { /* ignore */ }
      audioRef.current = null;
    }
    try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch { /* ignore */ }
    setSpeaking(false);
  }, []);

  // Ask the browser for microphone permission (shows the native popup).
  const requestMicPermission = useCallback(async () => {
    setError(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setMicStatus('denied');
      setError('Your browser does not expose a microphone API.');
      return false;
    }
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      s.getTracks().forEach((t) => t.stop());
      setMicStatus('granted');
      return true;
    } catch (e) {
      setMicStatus('denied');
      if (e && (e.name === 'NotAllowedError' || e.name === 'SecurityError')) {
        setError('Microphone access was blocked. Click the padlock icon next to the address bar and set Microphone to "Allow", then reload.');
      } else if (e && e.name === 'NotFoundError') {
        setError('No microphone detected on this device.');
      } else {
        setError(`Microphone error: ${e?.message || e}`);
      }
      return false;
    }
  }, []);

  // Probe current permission state on mount so we can show the right button.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (navigator.permissions?.query) {
          const status = await navigator.permissions.query({ name: 'microphone' });
          if (cancelled) return;
          setMicStatus(status.state); // 'granted' | 'denied' | 'prompt'
          status.onchange = () => !cancelled && setMicStatus(status.state);
        }
      } catch { /* permissions API not supported on this browser */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const speak = useCallback((text, language) =>
    new Promise(async (resolve) => {
      if (!text) return resolve();
      setSpeaking(true);

      // 1) Try OpenAI TTS via backend — natural, professional Sarah voice
      try {
        const res = await fetch(`${API_URL}/api/voice-agent/tts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text }),
        });
        if (res.ok) {
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          const audio = new Audio(url);
          audioRef.current = audio;
          const cleanup = () => {
            setSpeaking(false);
            audioRef.current = null;
            URL.revokeObjectURL(url);
            resolve();
          };
          audio.onended = cleanup;
          audio.onerror = cleanup;
          try {
            await audio.play();
            return;
          } catch {
            cleanup();
            return;
          }
        }
      } catch { /* fall through to browser fallback */ }

      // 2) Fallback: default Web Speech voice
      if (!('speechSynthesis' in window)) { setSpeaking(false); return resolve(); }
      try {
        window.speechSynthesis.cancel();
        const u = new SpeechSynthesisUtterance(text);
        u.lang = langToBCP47(language || langRef.current);
        u.rate = 1.0;
        const done = () => { setSpeaking(false); resolve(); };
        u.onend = done;
        u.onerror = done;
        window.speechSynthesis.speak(u);
      } catch {
        setSpeaking(false);
        resolve();
      }
    }),
  []);

  const listenOnce = useCallback(() => {
    const r = recognitionRef.current;
    if (!r) { setError('Voice input not supported in this browser'); return; }
    if (recognitionActiveRef.current) { try { r.abort(); } catch { /* ignore */ } }
    // Stop Sarah talking if user is interrupting
    stopSpeaking();
    r.lang = langToBCP47(langRef.current);
    r.onresult = (ev) => {
      const text = ev.results?.[0]?.[0]?.transcript || '';
      if (text.trim()) {
        setTranscript((prev) => [...prev, { role: 'user', content: text }]);
        sendTurnRef.current?.(text);
      }
    };
    try {
      r.start();
    } catch (e) {
      // InvalidStateError: already started — retry after a short delay
      setTimeout(() => { try { r.start(); } catch { /* ignore */ } }, 250);
    }
  }, [stopSpeaking]);

  // sendTurn needs to reference listenOnce, so use a ref to break the cycle
  const sendTurnRef = useRef(null);

  const sendTurn = useCallback(async (text) => {
    setThinking(true);
    try {
      const res = await fetch(`${API_URL}/api/voice-agent/turn`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          call_id: callIdRef.current,
          transcript: text,
          provider: 'web',
        }),
      });
      if (!res.ok) throw new Error(`turn status ${res.status}`);
      const data = await res.json();
      if (data.language) setLang(data.language);
      setTranscript((prev) => [...prev, { role: 'assistant', content: data.say }]);
      await speak(data.say_spoken || data.say, data.language);
      if (data.transfer_to_human) {
        setTranscript((prev) => [
          ...prev,
          { role: 'system', content: `↪︎ Transferring to ${data.transfer_to_number || '01908 220000'}…` },
        ]);
        await endCallRef.current?.();
        return;
      }
      if (data.ready_to_close) {
        await endCallRef.current?.();
      } else {
        // Auto-listen after Sarah finishes speaking
        listenOnce();
      }
    } catch (e) {
      setError(e.message || 'Could not reach the AI');
    } finally {
      setThinking(false);
    }
  }, [speak, listenOnce]);

  useEffect(() => { sendTurnRef.current = sendTurn; }, [sendTurn]);

  const endCall = useCallback(async () => {
    setCallActive(false);
    setListening(false);
    setSpeaking(false);
    setThinking(false);
    stopSpeaking();
    try { recognitionRef.current && recognitionRef.current.abort(); } catch { /* ignore */ }
    if (callIdRef.current) {
      try {
        await fetch(`${API_URL}/api/voice-agent/finalize`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ call_id: callIdRef.current }),
        });
      } catch { /* best effort */ }
    }
  }, [stopSpeaking]);

  const endCallRef = useRef(null);
  useEffect(() => { endCallRef.current = endCall; }, [endCall]);

  const startCall = async () => {
    setError(null);
    setTranscript([]);
    // Make sure the browser is happy with mic access before we start
    const allowed = await requestMicPermission();
    if (!allowed) return;
    callIdRef.current = `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setCallActive(true);
    await sendTurn(''); // empty transcript triggers the opening greeting
  };

  const onTalkClick = () => {
    if (!callActive) return;
    if (listening) {
      // Stop listening (mic icon already pressed)
      try { recognitionRef.current && recognitionRef.current.stop(); } catch { /* ignore */ }
      setListening(false);
      return;
    }
    listenOnce();
  };

  const headerStatus = !callActive
    ? 'Browser test · 3CX bridge ready'
    : listening
      ? 'Listening…'
      : speaking
        ? 'Speaking…'
        : thinking
          ? 'Thinking…'
          : 'Live';

  return (
    <div
      data-testid="voice-agent-test-widget"
      className="fixed inset-x-2 bottom-2 top-16 sm:inset-x-auto sm:bottom-24 sm:right-6 sm:top-auto sm:w-[400px] sm:h-[560px] z-[70] flex flex-col bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
    >
      <div className="flex items-center justify-between gap-2 px-4 py-3 bg-gradient-to-r from-indigo-600 to-blue-600 text-white">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center">
            <Phone className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-sm">Sarah · Flight 380 AI</div>
            <div className="text-[11px] text-white/80">{headerStatus}</div>
          </div>
        </div>
        <button onClick={onClose} aria-label="Close" data-testid="voice-agent-close" className="p-1.5 hover:bg-white/15 rounded-lg">
          <X className="h-4 w-4" />
        </button>
      </div>

      {!supported && (
        <div className="m-3 p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-xs flex gap-2">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          Your browser doesn't support Web Speech API. Try Chrome or Edge on desktop. The 3CX phone version will work everywhere.
        </div>
      )}

      {supported && !callActive && (micStatus === 'prompt' || micStatus === 'unknown') && (
        <div data-testid="mic-permission-prompt" className="mx-3 mt-3 p-3 bg-blue-50 border border-blue-200 rounded-xl text-blue-900 text-xs flex items-start gap-2">
          <Mic className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold mb-1">Microphone permission needed</div>
            <div className="mb-2">Sarah listens through your microphone. Click below and pick <b>"Allow"</b> when your browser asks.</div>
            <button
              type="button"
              onClick={requestMicPermission}
              data-testid="mic-allow-button"
              className="px-3 py-1.5 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition"
            >
              Allow microphone
            </button>
          </div>
        </div>
      )}

      {supported && micStatus === 'denied' && !callActive && (
        <div data-testid="mic-permission-denied" className="mx-3 mt-3 p-3 bg-red-50 border border-red-200 rounded-xl text-red-800 text-xs flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold mb-1">Microphone is blocked</div>
            <div className="mb-2">
              Click the <b>padlock 🔒</b> in your browser's address bar → set <b>Microphone</b> to <b>"Allow"</b> → reload the page.
              On Chrome you can also paste <code className="bg-white px-1 rounded">chrome://settings/content/microphone</code> in the address bar and remove the block.
            </div>
            <button
              type="button"
              onClick={requestMicPermission}
              data-testid="mic-retry-button"
              className="px-3 py-1.5 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 transition"
            >
              Try again
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-slate-50/40">
        {transcript.length === 0 && (
          <div className="text-center text-xs text-slate-500 mt-10 px-6">
            Press the green button below to start a test call.
            <br />Sarah will greet you in English — reply in English, Urdu, Hindi or Punjabi.
            <br /><br />Ask anything about <b>flights</b>, <b>Umrah</b> or <b>holidays</b>.
            Say <b>"speak to staff"</b> at any time and Sarah will transfer you to 01908 220000.
          </div>
        )}
        {transcript.map((t, i) => (
          <div key={i} className={`flex ${t.role === 'user' ? 'justify-end' : t.role === 'system' ? 'justify-center' : 'justify-start'}`}>
            <div
              className={`max-w-[85%] rounded-xl px-3 py-2 text-sm shadow-sm ${
                t.role === 'user'
                  ? 'bg-blue-600 text-white rounded-br-sm'
                  : t.role === 'system'
                  ? 'bg-amber-100 text-amber-800 text-xs italic'
                  : 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm'
              }`}
            >
              {t.content}
            </div>
          </div>
        ))}
        {thinking && (
          <div className="flex justify-start">
            <div className="bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs flex items-center gap-2 text-slate-500 shadow-sm">
              <Loader2 className="h-3 w-3 animate-spin text-indigo-500" /> Thinking…
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="mx-3 mb-2 text-[11px] text-red-600" data-testid="voice-agent-error">{error}</div>
      )}

      <div className="border-t border-slate-200 p-3 flex items-center gap-2 bg-white">
        {!callActive ? (
          <Button onClick={startCall} disabled={!supported} data-testid="voice-start" className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white">
            <Phone className="h-4 w-4 mr-2" /> Start test call
          </Button>
        ) : (
          <>
            <Button
              onClick={onTalkClick}
              disabled={thinking}
              variant={listening ? 'destructive' : 'outline'}
              data-testid="voice-talk"
              className="flex-1"
            >
              {listening ? <MicOff className="h-4 w-4 mr-2" /> : <Mic className="h-4 w-4 mr-2" />}
              {listening ? 'Stop' : speaking ? 'Interrupt' : 'Talk'}
            </Button>
            <Button onClick={endCall} variant="destructive" data-testid="voice-end">
              <PhoneOff className="h-4 w-4 mr-2" /> End
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default VoiceAgentTestWidget;
